import { useContext, useState, type ReactNode } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { LayoutCardContext } from '@/components/dashboard-layout/layout-card-context';
import { cn } from '@/lib/utils';

export interface CardProps {
    title?: string;
    /** Optional supporting line under the title (only when it adds info). */
    subtitle?: string;
    /** Muted text at the right of the header bar, before the actions (for
     *  example the source or the as-of date). */
    meta?: ReactNode;
    /** Right-aligned header controls (filters, buttons). */
    actions?: ReactNode;
    /** Optional footer region with a hairline top border. */
    footer?: ReactNode;
    children: ReactNode;
    className?: string;
    /** Drops the body padding. Use it when the card holds a table. */
    noPadding?: boolean;
    /**
     * Opt-in hover affordance. Most cards are static containers (a chart, a
     * table, a stat group) and must stay flat — a panel that lights up on hover
     * but does nothing is a false affordance. Set this only when the whole card
     * is genuinely a link/button.
     */
    interactive?: boolean;
    /**
     * The card opens and closes, and THE TITLE IS THE BUTTON. Use it for a list
     * that grows without a limit and that is not the whole subject of the page:
     * closed, the card costs one line, and the reader opens it for the work.
     *
     * The title alone names the button, exactly as it does on `Disclosure`, so
     * a count in `actions` or in `subtitle` never renames the control. The
     * children and the footer render only when the card is open.
     */
    collapsible?: boolean;
    /** The state of a collapsible card at the first render. */
    defaultOpen?: boolean;
    /**
     * The card fills the height of its grid cell. The header keeps its height,
     * and the body takes the rest of the height and scrolls. With `noPadding`,
     * the children get the same scroll region with no padding.
     */
    fill?: boolean;
    /**
     * The card shows its header only: no subtitle, no body and no footer. A
     * dashboard sets it on a card that has no items, so that the card costs
     * one line.
     */
    collapsed?: boolean;
}

// Card is the panel of the kit: a 1px border, a 4px radius, a 24px header
// bar (title left; meta and actions right) and a body with 8px padding, or no
// padding with `noPadding` when it holds a table.
//
// In the canvas of an editable dashboard, `LayoutCardContext` gives the card
// its cell. The card fills the cell, and it can collapse. In edit mode the
// layout controls replace the meta and the actions, and the body is inert.
// Outside a canvas the context is null and the DOM does not change.
export default function Card({
    title,
    subtitle,
    meta,
    actions,
    footer,
    children,
    className,
    noPadding = false,
    interactive = false,
    collapsible = false,
    defaultOpen = false,
    fill = false,
    collapsed = false,
}: CardProps) {
    const layout = useContext(LayoutCardContext);
    const [open, setOpen] = useState(defaultOpen);
    const fills = fill || layout !== null;
    const isCollapsed = collapsed || (layout?.collapsed ?? false);
    // While the user edits the layout, a press in the body must not open a
    // row or start an action. `inert` also removes the body from the tab order.
    const editing = layout?.editing ?? false;
    const leading = layout?.leading ?? null;
    const trailing = layout?.trailing ?? null;
    // A card with no title has no button to open it, so it always shows its
    // body. This keeps a caller from hiding content behind nothing.
    const canCollapse = collapsible && Boolean(title);
    const isOpen = !isCollapsed && (!canCollapse || open);
    const Chevron = isOpen ? ChevronDown : ChevronRight;
    const hasHeader = Boolean(title || actions || meta || leading || trailing);
    const heading = (
        <div className="min-w-0">
            {title &&
                (canCollapse ? (
                    <h2>
                        <button
                            type="button"
                            aria-expanded={isOpen}
                            onClick={() => setOpen(!open)}
                            className="flex min-h-6 items-center gap-1 hover:text-[rgb(var(--color-accent))]"
                        >
                            <Chevron className="h-3.5 w-3.5 shrink-0" aria-hidden />
                            {title}
                        </button>
                    </h2>
                ) : (
                    <h2 className="truncate">{title}</h2>
                ))}
            {/* The subtitle says what the BODY holds, so a closed
                card drops it and keeps one line. The closed state
                says what it holds through `actions`. */}
            {subtitle && isOpen && (
                <p className="truncate text-caption text-[rgb(var(--color-text-tertiary))]">
                    {subtitle}
                </p>
            )}
        </div>
    );
    const body = isOpen && (
        // A nested card in the body is not a cell of the canvas, so the body
        // clears the context.
        <LayoutCardContext.Provider value={null}>
            {fills ? (
                <div
                    className={cn(
                        'min-h-0 flex-1 overflow-y-auto',
                        !noPadding && 'septima-panel__body',
                        editing && 'opacity-60',
                    )}
                    inert={editing || undefined}
                >
                    {children}
                </div>
            ) : noPadding ? (
                children
            ) : (
                <div className="septima-panel__body">{children}</div>
            )}
        </LayoutCardContext.Provider>
    );
    return (
        <section
            className={cn(
                'septima-panel p-0',
                fills && 'flex h-full flex-col overflow-hidden',
                interactive &&
                    'transition-[border-color,background-color] duration-card-hover hover:border-[var(--septima-border-strong)]',
                className,
            )}
        >
            {hasHeader && (
                // In view mode a collapsed card in a cell is its header only, so
                // the header takes the full height of the cell. In edit mode the
                // cell keeps the height of the open card, and the header stays
                // at the top.
                <div
                    className={cn(
                        'septima-panel__header',
                        fills && (isCollapsed && !editing ? 'flex-1' : 'shrink-0'),
                    )}
                >
                    {leading ? (
                        <div className="flex min-w-0 items-center gap-1">
                            {leading}
                            {heading}
                        </div>
                    ) : (
                        heading
                    )}
                    {/* In edit mode the layout controls replace the meta and
                        the actions, so that a narrow card keeps room for its
                        title. */}
                    {((!editing && (meta || actions)) || trailing) && (
                        <div className="flex flex-shrink-0 items-center gap-2 text-caption">
                            {meta && !editing && (
                                <span className="whitespace-nowrap text-[rgb(var(--color-text-tertiary))]">
                                    {meta}
                                </span>
                            )}
                            {!editing && actions}
                            {trailing}
                        </div>
                    )}
                </div>
            )}
            {body}
            {isOpen && footer && (
                <div
                    className={cn(
                        'border-t border-[rgb(var(--color-divider))] px-2 py-1.5',
                        fills && 'shrink-0',
                        editing && 'opacity-60',
                    )}
                    inert={editing || undefined}
                >
                    {footer}
                </div>
            )}
        </section>
    );
}
